import type { CSSProperties } from 'react';
import { balances, myUnit, simplify, unitWeightTotal } from '../lib/split';
import { money, sharesLabel } from '../lib/money';
import { Empty, Note, Section, UnitAvatar } from '../components/ui';
import type { TripData } from '../types';
import type { SettleTarget } from './Trip';

export default function Balances({ data, uid, onSettle }: {
  data: TripData;
  uid: string;
  onSettle: (t: SettleTarget) => void;
}) {
  const { group, units, expenses, settlements } = data;
  const net = balances(units, expenses, settlements);
  const plan = simplify(net);
  const mine = myUnit(units, uid);
  const v = mine ? net[mine.id] ?? 0 : 0;
  const max = Math.max(1, ...Object.values(net).map(Math.abs));

  const ordered = units.slice().sort((a, b) => (net[b.id] ?? 0) - (net[a.id] ?? 0));

  return (
    <>
      {mine && (
        <div className="hero" style={{ marginTop: 2 }}>
          <div className="k">
            {v > 0 ? 'Your family gets back' : v < 0 ? 'Your family owes' : 'Your family is'}
          </div>
          <div className={`v amt ${v > 0 ? 'pos' : v < 0 ? 'neg' : 'zero'}`}>
            {v === 0 ? 'Square' : money(Math.abs(v), group.currency)}
          </div>
          <div className="n">{mine.name} · settles as one</div>
        </div>
      )}

      <Section label="Where everyone stands" />
      <div className="list">
        {ordered.map((u) => {
          const n = net[u.id] ?? 0;
          return (
            <div className="bal-row" key={u.id} style={{ '--uc': `var(--u${u.color})` } as CSSProperties}>
              <UnitAvatar unit={u} />
              <span className="nm">
                <span className="t">
                  {u.name}{mine?.id === u.id && <span className="muted"> · you</span>}
                </span>
                <span className="s">
                  {n > 0 ? 'gets back' : n < 0 ? 'owes' : 'settled'} · {sharesLabel(unitWeightTotal(u))}
                </span>
                <span className="bar">
                  <i style={{ width: `${Math.round((Math.abs(n) / max) * 100)}%` }} />
                </span>
              </span>
              <span className={`v amt ${n > 0 ? 'pos' : n < 0 ? 'neg' : 'zero'}`}>
                {n === 0 ? '—' : money(Math.abs(n), group.currency)}
              </span>
            </div>
          );
        })}
      </div>

      <Section
        label="Settle up"
        action={<span className="muted" style={{ fontSize: 12 }}>
          {plan.length} payment{plan.length === 1 ? '' : 's'}
        </span>}
      />
      <div className="list">
        {plan.length === 0 ? (
          <Empty title="All settled up">Every family is square. Nothing to transfer.</Empty>
        ) : (
          plan.map((t) => (
            <div className="settle" key={`${t.from}-${t.to}`}>
              <span className="who">
                <b>{units.find((u) => u.id === t.from)?.name}</b> pays{' '}
                <b>{units.find((u) => u.id === t.to)?.name}</b>
                <br />
                <span className="amt" style={{ fontSize: 15, fontWeight: 700 }}>
                  {money(t.amountMinor, group.currency)}
                </span>
              </span>
              <button
                className="btn-sm"
                onClick={() => onSettle({
                  fromUnitId: t.from, toUnitId: t.to, amountMinor: t.amountMinor
                })}
              >
                Record
              </button>
            </div>
          ))
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <Note>
          Balances are between <b>families</b>, not people
          {mine && mine.people.length > 1 && <> — {mine.people.map((p) => p.name).join(' and ')} never
          owe each other</>}. The settle-up plan uses the fewest transfers that clear everyone.
        </Note>
      </div>
    </>
  );
}
